import { isLocalTimeZone, normalizeTimeZone } from "@chronosphereio/core";
import { endOfMonth, isValid, milliseconds, setMilliseconds, startOfMonth, sub } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
const MINUTE_MS = 60000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 86400000;
const WEEK_MS = 604800000;
const MONTH_MS = 2629440000;
const YEAR_MS = 31536000000;
var time_PinStatus = /*#__PURE__*/ function(PinStatus) {
    PinStatus["PINNED"] = "pinned";
    PinStatus["PINNED_IN_DIFFERENT_TAB"] = "pinned_in_different_tab";
    PinStatus["UNPINNED"] = "unpinned";
    return PinStatus;
}({});
function isRelativeTimeRange(timeRange) {
    return void 0 !== timeRange.pastDuration;
}
function isAbsoluteTimeRange(timeRange) {
    return void 0 !== timeRange.start && void 0 !== timeRange.end;
}
function toAbsoluteTimeRange(timeRange) {
    const end = timeRange.end ?? setMilliseconds(new Date(), 0);
    return {
        start: sub(end, parseDurationString(timeRange.pastDuration)),
        end
    };
}
var time_DURATION_UNIT = /*#__PURE__*/ function(DURATION_UNIT) {
    DURATION_UNIT["MILLISECOND"] = "ms";
    DURATION_UNIT["SECOND"] = "s";
    DURATION_UNIT["MINUTE"] = "m";
    DURATION_UNIT["HOUR"] = "h";
    DURATION_UNIT["DAY"] = "d";
    DURATION_UNIT["WEEK"] = "w";
    DURATION_UNIT["MONTH"] = "M";
    DURATION_UNIT["YEAR"] = "y";
    return DURATION_UNIT;
}({});
const DURATION_REGEX = new RegExp("^(?:(\\d+)y)?(?:(\\d+)M)?(?:(\\d+)w)?(?:(\\d+)d)?(?:(\\d+)h)?(?:(\\d+)m)?(?:(\\d+)s)?(?:(\\d+)ms)?$");
function parseDurationString(durationString) {
    const matches = DURATION_REGEX.exec(durationString.trim());
    if (null === matches) throw new Error(`Invalid duration string '${durationString}'`);
    return {
        years: parseInt(matches[1] ?? '0'),
        months: parseInt(matches[2] ?? '0'),
        weeks: parseInt(matches[3] ?? '0'),
        days: parseInt(matches[4] ?? '0'),
        hours: parseInt(matches[5] ?? '0'),
        minutes: parseInt(matches[6] ?? '0'),
        seconds: parseInt(matches[7] ?? '0') + parseInt(matches[8] ?? '0') / 1000
    };
}
function getDurationStringMilliseconds(durationString) {
    if (void 0 === durationString || '' === durationString) return;
    const duration = parseDurationString(durationString);
    return milliseconds(duration);
}
function getDurationStringSeconds(durationString) {
    const durationMs = getDurationStringMilliseconds(durationString);
    if ('number' != typeof durationMs) return;
    return Math.floor(durationMs / 1000);
}
function isDurationString(maybeDuration) {
    if ('' === maybeDuration) return false;
    return DURATION_REGEX.test(maybeDuration);
}
function getDurationString(duration) {
    const result = `${duration.years ? `${duration.years}y` : ''}${duration.months ? `${duration.months}M` : ''}${duration.weeks ? `${duration.weeks}w` : ''}${duration.days ? `${duration.days}d` : ''}${duration.hours ? `${duration.hours}h` : ''}${duration.minutes ? `${duration.minutes}m` : ''}${duration.seconds ? `${duration.seconds}s` : ''}`;
    if ('' === result) return;
    return result;
}
const MAX_DATA_POINTS_HIGH_RESOLUTION = 1920;
const MIN_STEP_SIZE_MS = 15000;
const ROUNDED_STEP_INTERVALS = [
    {
        maxMs: 17500,
        roundedStepMs: MIN_STEP_SIZE_MS
    },
    {
        maxMs: 25000,
        roundedStepMs: 20000
    },
    {
        maxMs: 45000,
        roundedStepMs: 30000
    },
    {
        maxMs: 90000,
        roundedStepMs: MINUTE_MS
    },
    {
        maxMs: 210000,
        roundedStepMs: 120000
    },
    {
        maxMs: 450000,
        roundedStepMs: 300000
    },
    {
        maxMs: 750000,
        roundedStepMs: 600000
    },
    {
        maxMs: 1050000,
        roundedStepMs: 900000
    },
    {
        maxMs: 1500000,
        roundedStepMs: 1200000
    },
    {
        maxMs: 2700000,
        roundedStepMs: 1800000
    },
    {
        maxMs: 5400000,
        roundedStepMs: HOUR_MS
    },
    {
        maxMs: 9000000,
        roundedStepMs: 7200000
    },
    {
        maxMs: 16200000,
        roundedStepMs: 10800000
    },
    {
        maxMs: 32400000,
        roundedStepMs: 21600000
    },
    {
        maxMs: DAY_MS,
        roundedStepMs: 43200000
    },
    {
        maxMs: WEEK_MS,
        roundedStepMs: DAY_MS
    },
    {
        maxMs: 1814400000,
        roundedStepMs: WEEK_MS
    },
    {
        maxMs: 3628800000,
        roundedStepMs: 2592000000
    },
    {
        maxMs: 63072000000,
        roundedStepMs: 31536000000
    }
];
function roundStepInterval(stepMs) {
    for (const { maxMs, roundedStepMs } of ROUNDED_STEP_INTERVALS)if (stepMs < maxMs) return roundedStepMs;
    return MIN_STEP_SIZE_MS;
}
function getSuggestedStepMs(timeRange, maxDatapoints) {
    const queryRangeMs = timeRange.end.valueOf() - timeRange.start.valueOf();
    const stepMs = Math.floor(queryRangeMs / maxDatapoints);
    return roundStepInterval(stepMs);
}
function isValidDate(d) {
    return isValid(d);
}
function getMonthTimeRange(monthDate = new Date(), timeZone) {
    const normalizedTimeZone = normalizeTimeZone(timeZone);
    const localTimeZone = isLocalTimeZone(normalizedTimeZone);
    if ('UTC' === normalizedTimeZone) {
        const year = monthDate.getUTCFullYear();
        const month = monthDate.getUTCMonth();
        const start = new Date(Date.UTC(year, month, 1, 0, 0, 0, 0));
        const end = new Date(Date.UTC(year, month + 1, 0, 23, 59, 59, 0));
        return {
            start,
            end: setMilliseconds(end, 0)
        };
    }
    const zonedMonthDate = localTimeZone ? monthDate : utcToZonedTime(monthDate, normalizedTimeZone);
    const start = localTimeZone ? startOfMonth(zonedMonthDate) : utcToZonedTime(startOfMonth(zonedMonthDate), normalizedTimeZone);
    const end = localTimeZone ? endOfMonth(zonedMonthDate) : utcToZonedTime(endOfMonth(zonedMonthDate), normalizedTimeZone);
    return {
        start,
        end: setMilliseconds(end, 0)
    };
}
var time_DATE_FORMAT = /*#__PURE__*/ function(DATE_FORMAT) {
    DATE_FORMAT["YEAR"] = "yyyy";
    DATE_FORMAT["SHORT"] = "MM/dd/yyyy";
    DATE_FORMAT["MEDIUM"] = "MMM dd, yyyy";
    DATE_FORMAT["MEDIUM_NO_YEAR"] = "MMM dd";
    return DATE_FORMAT;
}({});
var time_TIME_FORMAT = /*#__PURE__*/ function(TIME_FORMAT) {
    TIME_FORMAT["SHORT_12"] = "hh:mm aa";
    TIME_FORMAT["SHORT_24"] = "HH:mm";
    TIME_FORMAT["MEDIUM_12"] = "hh:mm:ss aa";
    TIME_FORMAT["MEDIUM_24"] = "HH:mm:ss";
    return TIME_FORMAT;
}({});
const DATE_TIME_FORMAT_12 = "MM/dd/yyyy hh:mm:ss aa";
const DATE_TIME_FORMAT_12_SHORT_TIME = "MM/dd/yyyy hh:mm aa";
function areTimeRangesEqual(a, b) {
    if (a === b) return true;
    if (!a || !b) return false;
    if (isRelativeTimeRange(a) && isRelativeTimeRange(b)) return a.pastDuration === b.pastDuration && a.end?.getTime() === b.end?.getTime();
    if (isAbsoluteTimeRange(a) && isAbsoluteTimeRange(b)) {
        const startA = a.start instanceof Date ? a.start : new Date(a.start);
        const endA = a.end instanceof Date ? a.end : new Date(a.end);
        const startB = b.start instanceof Date ? b.start : new Date(b.start);
        const endB = b.end instanceof Date ? b.end : new Date(b.end);
        return startA.getTime() === startB.getTime() && endA.getTime() === endB.getTime();
    }
    return false;
}
export { time_DATE_FORMAT as DATE_FORMAT, DATE_TIME_FORMAT_12, DATE_TIME_FORMAT_12_SHORT_TIME, DAY_MS, time_DURATION_UNIT as DURATION_UNIT, HOUR_MS, MAX_DATA_POINTS_HIGH_RESOLUTION, MINUTE_MS, MIN_STEP_SIZE_MS, MONTH_MS, time_PinStatus as PinStatus, time_TIME_FORMAT as TIME_FORMAT, WEEK_MS, YEAR_MS, areTimeRangesEqual, getDurationString, getDurationStringMilliseconds, getDurationStringSeconds, getMonthTimeRange, getSuggestedStepMs, isAbsoluteTimeRange, isDurationString, isRelativeTimeRange, isValidDate, parseDurationString, roundStepInterval, toAbsoluteTimeRange };

//# sourceMappingURL=time.js.map